import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Activity, Wifi, WifiOff, Database, Server, Radio, Clock,
  RefreshCw, Cpu, HardDrive, Satellite, ArrowUpDown, Zap, AlertTriangle,
  Cloud, Timer, Archive
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatTimeOnly, formatDateOnly } from '../../utils/timezone';

const StatusDot = ({ status }) => {
  const color = status === 'connected' || status === 'healthy'
    ? 'bg-emerald-500' : status === 'error' ? 'bg-red-500' : 'bg-slate-400';
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${color} ${status === 'connected' || status === 'healthy' ? 'animate-pulse' : ''}`} />
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, status, testId }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700" data-testid={testId}>
    <div className="p-2 rounded-md bg-[#5388AF]/10">
      <Icon className="w-4 h-4 text-[#5388AF]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
    {status && <StatusDot status={status} />}
  </div>
);

const formatUptime = (seconds) => {
  if (!seconds) return '0s';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
};

const DiskUsageBar = ({ percent, status }) => {
  const barColor = status === 'critical'
    ? 'bg-red-500'
    : status === 'warning'
      ? 'bg-amber-500'
      : 'bg-emerald-500';
  return (
    <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div
        className={`h-full ${barColor} transition-all`}
        style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
      />
    </div>
  );
};

export const SystemHealth = () => {
  const { api } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await api.get('/system/health');
      setHealth(res.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchHealth, 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchHealth]);

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="health-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !health) {
    return (
      <Card><CardContent className="py-12 text-center">
        <p className="text-red-500" data-testid="health-error">{error}</p>
        <Button onClick={fetchHealth} className="mt-4" variant="outline">Retry</Button>
      </CardContent></Card>
    );
  }

  const h = health;
  const diskStatus = h.disk?.status || 'healthy';
  const diskWorstPercent = h.disk?.worst_percent_used ?? 0;
  const diskMounts = h.disk?.mounts || [];
  const overallStatus = (
    h.redis.status === 'connected'
    && h.database.status === 'connected'
    && diskStatus !== 'critical'
  ) ? 'healthy' : 'degraded';

  return (
    <div className="space-y-4" data-testid="system-health-panel">
      {/* Critical disk warning banner */}
      {diskStatus === 'critical' && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg border-2 border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800"
          data-testid="disk-critical-banner"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-red-900 dark:text-red-400">
              Disk usage critical — {diskWorstPercent}% full
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
              MongoDB may crash silently once disk hits 100%. Free up space immediately or expand the volume.
            </p>
          </div>
        </div>
      )}
      {diskStatus === 'warning' && (
        <div
          className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800"
          data-testid="disk-warning-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-900 dark:text-amber-400">
              Disk usage high — {diskWorstPercent}% full
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-500 mt-0.5">
              Consider clearing caches, rotating logs, or expanding storage before the pod fills up.
            </p>
          </div>
        </div>
      )}
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Badge className={overallStatus === 'healthy' 
            ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800' 
            : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'}
            data-testid="overall-status-badge"
          >
            <StatusDot status={overallStatus === 'healthy' ? 'connected' : 'error'} />
            <span className="ml-1.5">{overallStatus === 'healthy' ? 'All Systems Operational' : 'Degraded'}</span>
          </Badge>
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Updated {formatTimeOnly(lastUpdated, { seconds: true })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`gap-1.5 text-xs ${autoRefresh ? 'border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400' : ''}`}
            data-testid="auto-refresh-toggle"
          >
            <Activity className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchHealth} className="gap-1.5 text-xs" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Infrastructure Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Server */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Server className="w-4 h-4 text-[#5388AF]" /> Server
            </div>
            <MetricCard icon={Clock} label="Uptime" value={formatUptime(h.server.uptime_seconds)} testId="metric-uptime" />
          </CardContent>
        </Card>

        {/* Redis */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Zap className="w-4 h-4 text-[#5388AF]" /> Redis Pub/Sub
              <StatusDot status={h.redis.status} />
            </div>
            <MetricCard
              icon={h.redis.status === 'connected' ? Wifi : WifiOff}
              label="Status"
              value={h.redis.status === 'connected' ? 'Connected' : 'Offline'}
              sub={h.redis.latency_ms != null ? `Latency: ${h.redis.latency_ms}ms` : 'Single instance mode'}
              status={h.redis.status}
              testId="metric-redis"
            />
            {h.redis.channels && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {h.redis.channels.map(ch => (
                  <Badge key={ch} variant="outline" className="text-[10px] font-mono px-1.5 py-0.5">
                    {ch}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Database */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Database className="w-4 h-4 text-[#5388AF]" /> MongoDB
              <StatusDot status={h.database.status} />
            </div>
            <MetricCard
              icon={Database}
              label="Status"
              value={h.database.status === 'connected' ? 'Connected' : 'Offline'}
              sub={h.database.latency_ms != null ? `Latency: ${h.database.latency_ms}ms` : null}
              status={h.database.status}
              testId="metric-database"
            />
          </CardContent>
        </Card>
      </div>

      {/* WebSockets + TCP Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* WebSocket Connections */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ArrowUpDown className="w-4 h-4 text-[#5388AF]" /> WebSocket Connections
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MetricCard icon={Radio} label="Device WS" value={h.websockets.devices.connections}
                sub={`${h.websockets.devices.active_users} users`} testId="metric-device-ws" />
              <MetricCard icon={Wifi} label="Chat WS" value={h.websockets.chat.connections}
                sub={`${h.websockets.chat.active_users} users`} testId="metric-chat-ws" />
              <MetricCard icon={Satellite} label="Subscribed Devices" value={h.websockets.devices.subscribed_devices}
                testId="metric-subscribed" />
              <MetricCard icon={Cpu} label="Messages Sent" value={h.websockets.devices.messages_sent.toLocaleString()}
                testId="metric-messages" />
            </div>
          </CardContent>
        </Card>

        {/* TCP Connections */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <HardDrive className="w-4 h-4 text-[#5388AF]" /> TCP Device Connections
              <Badge variant="outline" className="ml-auto text-xs">{h.tcp.total_connections} Total</Badge>
            </div>
            {Object.entries(h.tcp.protocols).map(([proto, info]) => (
              <div key={proto} className="flex items-center justify-between p-2.5 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                data-testid={`tcp-proto-${proto}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#5388AF]" />
                  <span className="text-sm font-medium capitalize">{proto}</span>
                  <span className="text-xs text-muted-foreground font-mono">:{info.port}</span>
                </div>
                <Badge variant="outline" className="tabular-nums">
                  {info.connections} conn
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Device Fleet Summary */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <Satellite className="w-4 h-4 text-[#5388AF]" /> Device Fleet
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MetricCard icon={HardDrive} label="Total Devices" value={h.devices.total} testId="metric-total-devices" />
            <MetricCard icon={Wifi} label="Online" value={h.devices.online}
              status={h.devices.online > 0 ? 'connected' : 'disconnected'} testId="metric-online-devices" />
            <MetricCard icon={WifiOff} label="Offline" value={h.devices.offline} testId="metric-offline-devices" />
          </div>
        </CardContent>
      </Card>

      {/* Disk Usage — added Feb 2026 to catch pod-fills-up-and-Mongo-crashes pattern */}      <Card className="border-slate-200 dark:border-slate-700" data-testid="disk-usage-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <HardDrive className="w-4 h-4 text-[#5388AF]" /> Disk Usage
            <StatusDot status={diskStatus === 'healthy' ? 'connected' : 'error'} />
            <Badge
              variant="outline"
              className={`ml-auto tabular-nums text-xs ${
                diskStatus === 'critical'
                  ? 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-400'
                  : diskStatus === 'warning'
                    ? 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400'
                    : 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400'
              }`}
              data-testid="disk-worst-badge"
            >
              {diskWorstPercent}% worst mount
            </Badge>
          </div>
          {diskMounts.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="disk-empty-note">
              No mount data returned.
            </p>
          ) : (
            <div className="space-y-3">
              {diskMounts.map((m) => {
                const pct = m.percent_used ?? 0;
                const mountStatus = pct >= 95 ? 'critical' : pct >= 85 ? 'warning' : 'healthy';
                return (
                  <div
                    key={m.path}
                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                    data-testid={`disk-mount-${m.label}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{m.path}</span>
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">
                          ({m.label})
                        </span>
                      </div>
                      <span
                        className={`text-sm font-semibold tabular-nums ${
                          mountStatus === 'critical'
                            ? 'text-red-600 dark:text-red-400'
                            : mountStatus === 'warning'
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-foreground'
                        }`}
                      >
                        {pct}%
                      </span>
                    </div>
                    <DiskUsageBar percent={pct} status={mountStatus} />
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground tabular-nums">
                      <span>{formatBytes(m.used_bytes)} used</span>
                      <span>{formatBytes(m.free_bytes)} free</span>
                      <span>{formatBytes(m.total_bytes)} total</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* S3 Storage — added Feb 2026 for bucket size + video retention visibility */}
      <Card className="border-slate-200 dark:border-slate-700" data-testid="s3-storage-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <Cloud className="w-4 h-4 text-[#5388AF]" /> S3 Storage
            {h.s3?.enabled ? (
              <StatusDot status={
                h.s3.lifecycle_status === 'missing' || h.s3.lifecycle_status === 'error'
                  ? 'error' : 'connected'
              } />
            ) : (
              <StatusDot status="disconnected" />
            )}
            {h.s3?.bucket && (
              <Badge variant="outline" className="ml-auto text-xs font-mono" data-testid="s3-bucket-name">
                {h.s3.bucket}
              </Badge>
            )}
          </div>

          {!h.s3?.enabled ? (
            <p className="text-xs text-muted-foreground" data-testid="s3-disabled-note">
              S3 not configured on this server ({h.s3?.reason || 'unknown'}).
            </p>
          ) : (
            <div className="space-y-3">
              {/* Bucket size + object count */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <MetricCard
                  icon={Archive}
                  label="Total Size (StandardStorage)"
                  value={h.s3.total_bytes != null ? formatBytes(h.s3.total_bytes) : '—'}
                  sub={h.s3.metrics_as_of ? `as of ${formatDateOnly(h.s3.metrics_as_of)}` : 'CloudWatch pending'}
                  testId="s3-metric-size"
                />
                <MetricCard
                  icon={Database}
                  label="Object Count"
                  value={h.s3.object_count != null ? h.s3.object_count.toLocaleString() : '—'}
                  sub={h.s3.region ? `Region: ${h.s3.region}` : null}
                  testId="s3-metric-objects"
                />
              </div>

              {/* Lifecycle rules — video retention */}
              <div
                className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                data-testid="s3-lifecycle-block"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Timer className="w-4 h-4 text-[#5388AF]" />
                  <span className="text-sm font-medium">Retention Rules (Lifecycle)</span>
                  {h.s3.lifecycle_status === 'missing' && (
                    <Badge variant="outline" className="ml-auto text-xs border-red-300 text-red-700 dark:border-red-800 dark:text-red-400">
                      Missing — objects live forever
                    </Badge>
                  )}
                  {h.s3.lifecycle_status === 'error' && (
                    <Badge variant="outline" className="ml-auto text-xs border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400">
                      Read error
                    </Badge>
                  )}
                  {h.s3.lifecycle_status === 'no_rules' && (
                    <Badge variant="outline" className="ml-auto text-xs border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400">
                      No enabled rules
                    </Badge>
                  )}
                </div>

                {h.s3.lifecycle_rules && h.s3.lifecycle_rules.length > 0 ? (
                  <div className="space-y-1.5">
                    {h.s3.lifecycle_rules.map((rule) => (
                      <div
                        key={rule.id || rule.prefix}
                        className="flex items-center justify-between text-xs gap-3"
                        data-testid={`s3-lifecycle-rule-${(rule.prefix || 'root').replace(/\W/g, '_')}`}
                      >
                        <span className="font-mono text-muted-foreground truncate">
                          {rule.prefix || '(all objects)'}
                        </span>
                        <Badge variant="outline" className="tabular-nums whitespace-nowrap">
                          {rule.expiration_days} days
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No enabled expiration rules found on the bucket.
                  </p>
                )}

                {h.s3.next_video_expiry_in_days != null && (
                  <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Next video expiry wave</span>
                    <span
                      className={`font-semibold tabular-nums ${
                        h.s3.next_video_expiry_in_days <= 3
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-foreground'
                      }`}
                      data-testid="s3-next-expiry"
                    >
                      in {h.s3.next_video_expiry_in_days} day{h.s3.next_video_expiry_in_days === 1 ? '' : 's'}
                    </span>
                  </div>
                )}
              </div>

              {h.s3.errors && h.s3.errors.length > 0 && (
                <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-xs text-amber-700 dark:text-amber-400" data-testid="s3-errors">
                  <div className="font-medium mb-0.5">Partial data — errors:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {h.s3.errors.map((err, idx) => <li key={idx}>{err}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
